import {formatNumber} from '../../src/utils/number.ts';
import type {PageObjectWithLayout, PrintEditionData} from '../../types/types.ts';
import type {ContentAdapter} from '../adapters/content-adapter.ts';
import {Article, type Asset} from '../models/article.ts';
import {createElement} from './dom.ts';
import {LayoutParser} from './layout-parser.ts';
import type {Store} from './store.ts';

export function createArticleFromLayout(
	{layoutHtml, object}: PageObjectWithLayout,
	editionData: PrintEditionData,
	adapter: ContentAdapter,
	previousItems: Article[],
	store: Store,
	articleIdPrefix: string
) {
	const {uuid, version} = object.layoutPreview;
	const parser = new LayoutParser(layoutHtml, document);
	const el = createElement(document, 'div');
	el.innerHTML = layoutHtml;
	const headlineEl = parser.getHeadline();
	const title = parser.getText(
		headlineEl,
		':scope > h1:not(:has(p)), :scope > div > h1:not(:has(p)), :scope > div > h1 > p, :scope > span, :scope > p, .byline > .byline, .title h1'
	);

	const pretitle = parser.getText(headlineEl, 'h2') || parser.getKicker()?.innerText || '';
	const intro = el.querySelector<HTMLElement>('.intro')?.innerText || '';
	const bylines = [
		...Array.from<HTMLElement>(el.querySelectorAll('.byline-profile')).map((bylineEl: HTMLElement) => {
			return {
				author: bylineEl.querySelector<HTMLElement>('.author')?.innerText,
				email: bylineEl.querySelector<HTMLElement>('.email')?.innerText
			};
		}),
		...Array.from<HTMLElement>(el.querySelectorAll('.byline-labels')).map((bylineEl: HTMLElement) => {
			return {
				author: bylineEl.querySelector<HTMLElement>('p:nth-child(1)')?.innerText,
				label: bylineEl.querySelector<HTMLElement>('p:nth-child(2)')?.innerText
			};
		})
	];

	const assetEl = parser.getFirstAsset();
	let asset: Asset | undefined;
	if (assetEl) {
		const captionEl = parser.getFirstCaption();
		asset = {
			url: assetEl.querySelector('img')?.src,
			pictureHtml: assetEl.querySelector('.image-wrapper')?.innerHTML,
			captionHtml: captionEl?.innerHTML || assetEl.querySelector('.figcaption-inner')?.innerHTML
		};
	}

	const size = formatNumber((object.width * object.height) / 1000, 0);
	const item = editionData.items[object.itemId];
	const articleId = articleIdPrefix + item.sourceId;
	const itemFromPreviousPages = previousItems.find(({id}) => id === articleId);
	const firstPrintPage = editionData.pages[object.pages[0]];

	if (itemFromPreviousPages) {
		itemFromPreviousPages.size += size;
		return null;
	}

	const section = adapter.getSectionForPrintItem(firstPrintPage, item, parser);

	const sort = firstPrintPage.number + (1 - size / 1000);
	const data = {
		id: articleId,
		objectId: object._id,
		pages: object.pages.map((pageId) => editionData.pages[pageId].number),
		displayPages: object.pages.map((pageId) => editionData.pages[pageId].displayNumber),
		section,
		sort,
		publishedAt: editionData.publishDate,
		groupBy: section,
		story: adapter.getStoryForPrintItem(firstPrintPage, item, parser),
		layout: {
			html: layoutHtml,
			screenshot: object.imageUrl,
			uuid,
			version
		},
		byline: {
			html: parser.getByline(),
			items: bylines
		},
		asset: asset || undefined,
		pretitle,
		title,
		intro,
		size,
		data: item.data
	};
	return new Article(data, store.getItemState(data.id));
}
